import * as React from "react";
import {ITranslatedComponent} from "../../../redux/reducers/generalReducer";

import {Segment, Table} from "semantic-ui-react";

import {LanguageCnrDto, LanguageDto, ResourceListOfLanguageCnrDto} from "../../../services/Client";
import {translate} from "react-i18next";
import {compose, Dispatch} from "redux";
import {connect} from "react-redux";
import {IReduxState} from "../../../redux/IReduxState";
import {selectClient, selectLanguageList, selectUserSettings} from "../../../redux/selectors";
import LanguageSelection from "./LanguageSelection";
import AdminViewActions from "../../../redux/actions/AdminViewActions";
import {selectCnrLanguages} from "../../../redux/selectors/administrationSelectors";
import {getDtoArray} from "../AdminUtils";
import {IUserSettings} from "../../../data/IUserSettings";
import HATEOASClient from "../../../services/HATEOASClient";
import TextField from "../../../components/TextField";
import _ from "lodash";

interface IMapStateToProps {
    client: HATEOASClient;
    languageList?: LanguageDto[];
    cnrLanguageList?: ResourceListOfLanguageCnrDto;
    userSettings: IUserSettings;
}

interface IMapDispatchToProps {
    getCnrLanguages: () => void;
    updateCnrLanguages: (newDto: LanguageCnrDto) => void;
}

interface IOwnProps {
}

interface ILanguageTableState {
    showDetailsDialog?: LanguageCnrDto;
    cnrLanguageList: LanguageCnrDto[];
    /** Cnr filter. */
    filter: string | undefined;
}

export interface ILanguageTableProps extends ITranslatedComponent, IOwnProps, IMapStateToProps, IMapDispatchToProps {
}

export enum LanguageCodeEnum {
    BASIC_GERMAN = 'de',
    BASIC_ENGLISH = 'cc'
}

/**
 * A table-like component that displays a collection of LanguageCnrDtos in a
 * structured way and offers actions to assign languages to a cnr
 *
 */
export class LanguageTable extends React.Component<ILanguageTableProps, ILanguageTableState> {
    constructor(props: ILanguageTableProps) {
        super(props);

        this.state = {
            showDetailsDialog: undefined,
            cnrLanguageList: [],
            filter: undefined
        };
    }

    componentDidMount() {
        this.props.getCnrLanguages();
    }

    componentDidUpdate(prevProps: Readonly<ILanguageTableProps>, prevState: Readonly<ILanguageTableState>, snapshot?: any) {
        if (prevProps.cnrLanguageList !== this.props.cnrLanguageList) {
            const cnrLanguageList = getDtoArray<LanguageCnrDto>(this.props.cnrLanguageList);
            this.setState({cnrLanguageList: cnrLanguageList ? cnrLanguageList : []});
        }
    }

    renderHeader = (): JSX.Element => (
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell width='2'>{this.props.t("ADMIN_VIEW:CN")}</Table.HeaderCell>
                <Table.HeaderCell width='12'>{this.props.t("ADMIN_VIEW:LANGUAGES")}</Table.HeaderCell>
            </Table.Row>
        </Table.Header>
    )

    renderFilters = (): JSX.Element => (
        <TextField
            placeholder={this.props.t("FILTER_BY_CNR")}
            value={this.state.filter}
            onChange={(event: React.FormEvent<{}>, newValue: string) => {
                const filterValue = newValue !== "" ? newValue : undefined;
                let filteredDtos = getDtoArray<LanguageCnrDto>(this.props.cnrLanguageList);
                if (filterValue) {
                    filteredDtos = this.applyFilter(filteredDtos, filterValue);
                }
                this.setState({filter: filterValue, cnrLanguageList: filteredDtos});
            }}
        />
    )

    applyFilter = (dtos: LanguageCnrDto[], filter: string): LanguageCnrDto[] => {
        const filterLowerCase = filter.toLowerCase();
        return dtos.filter(dto => _.includes(dto.cnr.name.toLowerCase(), filterLowerCase));
    }

    renderRows = (dtos: LanguageCnrDto[]): JSX.Element[] => (
        dtos.map((dto, index) => (
            <Table.Row key={index} style={{backgroundColor: index % 2 === 0 ? 'white' : '#80808029'}}>
                <Table.Cell>{this.props.t(`GLOBAL:${dto.cnr.cnr}`)}</Table.Cell>
                <Table.Cell>{this.renderAssignedLanguages(dto)}</Table.Cell>
            </Table.Row>
        ))
    )

    renderTable = (dtos: LanguageCnrDto[]): JSX.Element => (

        <Table celled={true}>
            {this.renderHeader()}
            <Table.Body>
                {this.renderRows(dtos)}
            </Table.Body>
        </Table>
    )

    getFixedLanguages = (dtos: LanguageDto[]): LanguageDto[] => {
        return dtos.filter(dto => [LanguageCodeEnum.BASIC_GERMAN.toString(), LanguageCodeEnum.BASIC_ENGLISH.toString()].indexOf(dto.vmce) >= 0);
    }

    getEditableLanguages = (dtos: LanguageDto[]) => {
        return dtos.filter(dto => [LanguageCodeEnum.BASIC_GERMAN.toString(), LanguageCodeEnum.BASIC_ENGLISH.toString()].indexOf(dto.vmce) < 0);
    }

    renderAssignedLanguages = (dto: LanguageCnrDto): JSX.Element => (
        <div>
            <div>
                <b>{this.props.t("FIXED_LANGUAGES")}: </b>
                {this.props.client.PATCH_assignLanguageCnr(dto).canExecute() ? (
                    <LanguageSelection
                        readOnly={true}
                        placeholder={this.props.t("SELECT_LANGUAGES")}
                        languages={this.getFixedLanguages(this.props.languageList || [])}
                        values={this.getFixedLanguages(dto.assignedLanguages || [])}
                        userLanguage={this.props.userSettings.language}
                    />
                ) : (
                    dto.assignedLanguages &&
                    this.getFixedLanguages(dto.assignedLanguages || []).map((languageDto: LanguageDto) =>
                        this.props.userSettings.language === 'de' ? languageDto.nameDe : languageDto.nameEn
                    ).join(", ")
                )}
            </div>
            <div>
                <b>{this.props.t("EDITABLE_LANGUAGES")}: </b>
                {this.props.client.PATCH_assignLanguageCnr(dto).canExecute() ? (
                    <LanguageSelection
                        readOnly={dto.cnr.cnr === 'CXX'}
                        placeholder={this.props.t("SELECT_LANGUAGES")}
                        languages={this.getEditableLanguages(this.props.languageList || [])}
                        values={this.getEditableLanguages(dto.assignedLanguages || [])}
                        onChange={(assignedLanguages: LanguageDto[]) => {
                            dto.assignedLanguages = this.getFixedLanguages(dto.assignedLanguages || []);
                            assignedLanguages.forEach(val => {
                                if(dto.assignedLanguages) {
                                    dto.assignedLanguages.push(val);
                                }
                            });
                            this.props.updateCnrLanguages(dto);
                        }}
                        userLanguage={this.props.userSettings.language}
                    />
                ) : (
                    dto.assignedLanguages &&
                    this.getEditableLanguages(dto.assignedLanguages || []).map((languageDto: LanguageDto) =>
                         this.props.userSettings.language === 'de' ? languageDto.nameDe : languageDto.nameEn
                    ).join(", ")
                )}
            </div>
        </div>
    )

    render() {
        const {cnrLanguageList} = this.state;

        const filteredCnrLanguageList = this.state.filter
            ? this.applyFilter(cnrLanguageList, this.state.filter)
            : cnrLanguageList;

        return (
            <Segment>
                {this.renderFilters()}
                {this.renderTable(filteredCnrLanguageList ? filteredCnrLanguageList : [])}
            </Segment>
        );
    }
}

const mapStateToProps = (state: IReduxState): IMapStateToProps => ({
    client: selectClient(state),
    languageList: selectLanguageList(state),
    cnrLanguageList: selectCnrLanguages(state),
    userSettings: selectUserSettings(state)
});

const mapDispatchToProps = (dispatch: Dispatch<IReduxState>, ownProps: ILanguageTableProps): IMapDispatchToProps => ({
    getCnrLanguages: () => dispatch(AdminViewActions.GetCnrLanguages(ownProps.t)),
    updateCnrLanguages: (newDto: LanguageCnrDto) => dispatch(AdminViewActions.UpdateCnrLanguages(ownProps.t, newDto))
});

export default compose<React.ComponentType<IOwnProps>>(
    translate("LANGUAGES"),
    connect<IMapStateToProps, IMapDispatchToProps>(mapStateToProps, mapDispatchToProps)
)(LanguageTable);



// WEBPACK FOOTER //
// ./src/views/AdminView/LanguageTab/LanguageTable.tsx